import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Pagination,
  Skeleton,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconExternalLink,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { getSkypointPendingNotes } from "../../../services/skypointAlerts";
import { enterProviderView } from "../../../services/skypointProvider";

const LIMIT = 25;
const TOTAL_COLS = 6;

const useStyles = createStyles((theme) => ({
  container: { width: "100%" },
  stickyBanner: { position: "sticky", top: 0, zIndex: 100 },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: { padding: "16px 12px" },
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    "&:hover": { color: "#101828" },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 20,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "#FFF6ED",
  },
  title: {
    fontFamily: "Inter",
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.2,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
    marginTop: 2,
  },
  tableWrap: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    "& thead tr th": {
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F2F4F7",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  groupRow: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    userSelect: "none",
    "& td": {
      borderBottom: "1px solid #EAECF0 !important",
      padding: "10px 16px !important",
    },
    "&:hover td": {
      backgroundColor: "#F3F4F6",
    },
  },
  childRow: {
    "& td:first-of-type": {
      paddingLeft: "40px !important",
    },
  },
  emptyState: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#667085",
    fontFamily: "Inter",
    fontSize: 14,
  },
  paginationRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderTop: "1px solid #EAECF0",
  },
}));

const PendingNotes = ({ isSidebarOpen }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const data = useSelector((state) => state?.skypointAlerts?.pendingNotes) || {};
  const items = useMemo(() => data.items || [], [data.items]);
  const total = data.total || 0;
  const loading = !!data.loading;

  const [page, setPage] = useState(1);
  const [expandedProviders, setExpandedProviders] = useState(new Set());

  useEffect(() => {
    getSkypointPendingNotes({ page, limit: LIMIT });
  }, [page]);

  useEffect(() => {
    setExpandedProviders(new Set());
  }, [page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.providerId || item.providerName;
      if (!map.has(key)) {
        map.set(key, {
          key,
          providerId: item.providerId,
          providerName: item.providerName,
          notes: [],
        });
      }
      map.get(key).notes.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const toggleProvider = (key) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleViewProvider = async (providerId, providerName) => {
    const ok = await enterProviderView(providerId, providerName);
    if (ok) navigate("/home/provider-view");
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.stickyBanner}>
        <AppNavbar title="Home" profile={profile} isSidebarOpen={isSidebarOpen} />
      </Box>

      <Box className={classes.content}>
        <Box
          className={classes.backRow}
          onClick={() => navigate("/home/dashboard-skypoint")}
        >
          <IconArrowLeft size={16} />
          <span>Back to dashboard</span>
        </Box>

        <Box className={classes.headerRow}>
          <Box className={classes.headerIcon}>
            <IconClock size={22} color="#F79009" />
          </Box>
          <Box>
            <Text className={classes.title}>Pending Notes (&gt; 3 Days)</Text>
            <Text className={classes.subtitle}>
              Notes still awaiting approval that were created more than 3 days ago
              {total > 0 && ` · ${total} ${total === 1 ? "note" : "notes"}`}
            </Text>
          </Box>
        </Box>

        <Box className={classes.tableWrap}>
          <Table className={classes.table} verticalSpacing="md">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Note Type</th>
                <th>Author</th>
                <th style={{ width: 110 }}>Pending</th>
                <th style={{ width: 130 }}>Created</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={TOTAL_COLS}>
                      <Skeleton height={32} radius={6} />
                    </td>
                  </tr>
                ))
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={TOTAL_COLS} className={classes.emptyState}>
                    No old pending notes across your providers.
                  </td>
                </tr>
              ) : (
                groups.map((group) => {
                  const isExpanded = expandedProviders.has(group.key);
                  return (
                    <React.Fragment key={group.key}>
                      <tr
                        className={classes.groupRow}
                        onClick={() => toggleProvider(group.key)}
                      >
                        <td colSpan={TOTAL_COLS}>
                          <Flex align="center" gap={10}>
                            {isExpanded ? (
                              <IconChevronDown size={16} color="#667085" />
                            ) : (
                              <IconChevronRight size={16} color="#667085" />
                            )}
                            <Text weight={600} size="sm" color="#101828">
                              {group.providerName || "Unknown Provider"}
                            </Text>
                            <Badge size="sm" variant="filled" color="orange" radius="xl">
                              {group.notes.length}{" "}
                              {group.notes.length === 1 ? "note" : "notes"}
                            </Badge>
                          </Flex>
                        </td>
                      </tr>

                      {isExpanded &&
                        group.notes.map((item) => (
                          <tr
                            key={item.relatedId || item._id}
                            className={classes.childRow}
                          >
                            <td>
                              <Text size="sm" color={item.clientName ? "dark" : "dimmed"}>
                                {item.clientName || "—"}
                              </Text>
                            </td>
                            <td>
                              <Text size="sm">{item.noteType}</Text>
                            </td>
                            <td>
                              <Text size="sm" color={item.author ? "dark" : "dimmed"}>
                                {item.author || "—"}
                              </Text>
                            </td>
                            <td>
                              <Badge
                                color={
                                  item.daysPending >= 14
                                    ? "red"
                                    : item.daysPending >= 7
                                    ? "orange"
                                    : "yellow"
                                }
                                variant="light"
                                size="sm"
                              >
                                {item.daysPending}{" "}
                                {item.daysPending === 1 ? "day" : "days"}
                              </Badge>
                            </td>
                            <td>
                              <Text size="xs" color="dimmed">
                                {item.relatedDate
                                  ? new Date(item.relatedDate).toLocaleDateString()
                                  : "—"}
                              </Text>
                            </td>
                            <td>
                              <Button
                                size="xs"
                                variant="subtle"
                                leftIcon={<IconExternalLink size={14} />}
                                onClick={() =>
                                  handleViewProvider(item.providerId, item.providerName)
                                }
                              >
                                Ogiso Core
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Box className={classes.paginationRow}>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                size="sm"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PendingNotes;
